import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailPriceChanges, type PriceChangeListItem } from '@/api/retailPriceChanges';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.price_change.create';

export default function PriceChangesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-price-changes', orgId],
    queryFn: () => retailPriceChanges.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const pgQ = useQuery({
    queryKey: ['retail-price-groups', orgId],
    queryFn: () => retailPriceGroups.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });
  const pgName = useMemo(() => {
    const m = new Map<string, string>();
    pgQ.data?.items.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [pgQ.data]);

  const cols = useMemo<ColumnDef<PriceChangeListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) =>
          r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      {
        key: 'price_group_id',
        header: 'Цінова група',
        width: 200,
        sortValue: (r) => pgName.get(r.price_group_id) ?? '',
        cell: (r) => pgName.get(r.price_group_id) ?? '',
      },
      {
        key: 'effective_from',
        header: 'Діє з',
        width: 160,
        sortValue: (r) => r.effective_from ?? '',
        cell: (r) =>
          r.effective_from ? new Date(r.effective_from).toLocaleDateString('uk-UA') : '',
      },
      {
        key: 'change_reason',
        header: 'Причина',
        sortValue: (r) => r.change_reason ?? '',
        cell: (r) => r.change_reason,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 140,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [pgName],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати зміну цін (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Зміна цін
          <HintBadge
            title="Що це?"
            text={
              'Документ масової зміни роздрібних цін у обраній ціновій групі.\n\n' +
              'Замість того щоб правити кожну ціну окремо у «Роздрібні ціни» — ' +
              'створюєте один документ із переліком: товар → нова ціна.\n\n' +
              'Як працює:\n' +
              '• Оберіть цінову групу (наприклад «Роздріб»).\n' +
              '• Дата дії — з якого моменту нові ціни.\n' +
              '• Додайте товари + нові ціни (для кожного видно стару + % зміни).\n' +
              '• Проведення → старі ціни деактивуються, нові стають активними.\n' +
              '• Можна скасувати проведення (UNPOST) — повернеться попередній прайс.\n\n' +
              'Використовується для: сезонних знижок, переоцінки інфляції, акційних періодів.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/price-changes/new')}
        >
          + Нова зміна цін
        </button>
      </div>
      <DataTable<PriceChangeListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/retail/price-changes/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
